import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { GaitSession, StrideData, StepData, FootPressurePoint } from '../AdvancedGaitAnalysis';

interface PressureMappingProps {
  session: GaitSession;
  selectedStride: StrideData | undefined;
  isPlaying: boolean;
  playbackSpeed: number;
  onStrideChange: (strideId: string) => void;
}

const PressureMapping: React.FC<PressureMappingProps> = ({
  session,
  selectedStride,
  isPlaying,
  playbackSpeed,
  onStrideChange
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const [currentStepIndex, setCurrentStepIndex] = useState<0 | 1>(0); // 0 = left step, 1 = right step
  const [pressureScale, setPressureScale] = useState<'linear' | 'logarithmic'>('linear');
  const [displayMode, setDisplayMode] = useState<'2d' | '3d'>('2d');
  const [showCenterOfPressure, setShowCenterOfPressure] = useState<boolean>(true);
  
  // Get the current step being displayed
  const currentStep = selectedStride ? 
    (currentStepIndex === 0 ? selectedStride.leftStep : selectedStride.rightStep) : undefined;

  // Navigate between strides
  const handlePrevStride = () => {
    if (!selectedStride) return;
    
    const currentIndex = session.strides.findIndex(stride => stride.id === selectedStride.id);
    if (currentIndex > 0) {
      onStrideChange(session.strides[currentIndex - 1].id);
    }
  };

  const handleNextStride = () => {
    if (!selectedStride) return;
    
    const currentIndex = session.strides.findIndex(stride => stride.id === selectedStride.id);
    if (currentIndex < session.strides.length - 1) {
      onStrideChange(session.strides[currentIndex + 1].id);
    }
  };

  // Toggle between left and right step
  const toggleStep = () => {
    setCurrentStepIndex(prev => prev === 0 ? 1 : 0);
  };

  // Animation logic for playback
  useEffect(() => {
    if (!isPlaying || !selectedStride) return;
    
    let startTime = 0;
    let stepDuration = currentStep?.duration || 1000;
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      
      const elapsed = (timestamp - startTime) * playbackSpeed;
      
      // If we've completed this step, move to the next one
      if (elapsed >= stepDuration) {
        if (currentStepIndex === 0) {
          // Move to right step
          setCurrentStepIndex(1);
          startTime = timestamp;
        } else {
          // Move to next stride
          const currentStrideIndex = session.strides.findIndex(stride => stride.id === selectedStride.id);
          if (currentStrideIndex < session.strides.length - 1) {
            onStrideChange(session.strides[currentStrideIndex + 1].id);
          } else {
            // We've reached the end, stop playing
            return;
          }
          setCurrentStepIndex(0);
          startTime = timestamp;
        }
      }
      
      // Draw the current frame
      drawPressureData();
      
      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, currentStepIndex, selectedStride, playbackSpeed, session.strides]);

  // Draw the pressure data on the canvas
  const drawPressureData = () => {
    if (!canvasRef.current || !currentStep) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set canvas dimensions
    canvas.width = 400;
    canvas.height = 600;
    
    // Draw the foot outline
    const footOutlinePoints = generateFootOutline(currentStep.footSide, canvas.width, canvas.height);
    ctx.beginPath();
    ctx.moveTo(footOutlinePoints[0].x, footOutlinePoints[0].y);
    for (let i = 1; i < footOutlinePoints.length; i++) {
      ctx.lineTo(footOutlinePoints[i].x, footOutlinePoints[i].y);
    }
    ctx.closePath();
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Draw the pressure points
    currentStep.pressurePoints.forEach(point => {
      const normX = point.x / 100 * canvas.width;
      const normY = point.y / 100 * canvas.height;
      
      // Scale pressure according to the selected scale
      let scaledPressure = pressureScale === 'linear' 
        ? point.pressure / 100
        : Math.log10(1 + (point.pressure * 9)) / Math.log10(10);
      
      // Draw the pressure point
      const radius = 10 * scaledPressure + 2;
      ctx.beginPath();
      ctx.arc(normX, normY, radius, 0, 2 * Math.PI);
      
      // Create gradient color based on pressure
      // Red for high pressure, blue for low
      let r = Math.round(255 * scaledPressure);
      let g = Math.round(100 * (1 - scaledPressure));
      let b = Math.round(200 * (1 - scaledPressure));
      
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.7)`;
      ctx.fill();
    });
    
    // Draw center of pressure
    if (showCenterOfPressure && currentStep.centerOfPressure) {
      const cpX = currentStep.centerOfPressure.x / 100 * canvas.width;
      const cpY = currentStep.centerOfPressure.y / 100 * canvas.height;
      
      ctx.beginPath();
      ctx.arc(cpX, cpY, 8, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(255, 255, 0, 0.8)';
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Add label
      ctx.font = '12px Arial';
      ctx.fillStyle = '#333';
      ctx.fillText('COP', cpX + 12, cpY);
    }
  };
  
  // Generate a foot outline shape based on side
  const generateFootOutline = (side: 'left' | 'right', width: number, height: number) => {
    const scale = 0.8; // Scale factor for foot size
    const offset = side === 'left' ? 0.4 : 0.6; // Offset for left vs right foot
    
    // Create foot outline points (normalize to 0-1 range)
    const footShape = [
      { x: 0.5, y: 0.1 },  // Toe tip
      { x: 0.65, y: 0.15 }, // Toe right side
      { x: 0.7, y: 0.3 },  // Ball right
      { x: 0.6, y: 0.6 },  // Arch right
      { x: 0.55, y: 0.8 }, // Heel right
      { x: 0.5, y: 0.9 },  // Heel bottom
      { x: 0.45, y: 0.8 }, // Heel left
      { x: 0.4, y: 0.6 },  // Arch left
      { x: 0.3, y: 0.3 },  // Ball left
      { x: 0.35, y: 0.15 }  // Toe left side
    ];
    
    // Mirror for right foot
    const mirroredShape = side === 'right' 
      ? footShape.map(pt => ({ x: 1 - (pt.x - 0.5) + 0.5, y: pt.y }))
      : footShape;
    
    // Scale and position
    return mirroredShape.map(pt => ({
      x: (pt.x * scale + (offset - 0.5 * scale)) * width,
      y: pt.y * height * scale + height * (1 - scale) * 0.5
    }));
  };
  
  // Effect to draw the canvas when component mounts or when step changes
  useEffect(() => {
    drawPressureData();
  }, [currentStep, pressureScale, showCenterOfPressure, displayMode]);

  // Stop animation when component unmounts
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  if (!selectedStride) {
    return <div className="p-6 text-center text-gray-500">No stride data selected</div>;
  }

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row">
        {/* Canvas container */}
        <div className="flex-1 mb-6 lg:mb-0 lg:mr-6">
          <div className="bg-gray-50 border rounded-lg p-4 flex justify-center">
            <canvas 
              ref={canvasRef} 
              className="max-w-full" 
              style={{ 
                width: '400px', 
                height: '600px',
                filter: displayMode === '3d' ? 'blur(0.5px) drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none',
              }}
            />
          </div>
          
          {/* Step Navigation */}
          <div className="mt-4 flex justify-between items-center">
            <button
              onClick={handlePrevStride}
              className="p-2 rounded-full hover:bg-gray-100"
              disabled={session.strides.indexOf(selectedStride) === 0}
            >
              <ChevronLeft size={24} className={session.strides.indexOf(selectedStride) === 0 ? "text-gray-300" : "text-gray-600"} />
            </button>
            
            <div className="text-center">
              <div className="text-sm text-gray-500">
                Stride {session.strides.indexOf(selectedStride) + 1} of {session.strides.length}
              </div>
              <button
                onClick={toggleStep}
                className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                {currentStepIndex === 0 ? 'Left' : 'Right'} Foot
              </button>
            </div>
            
            <button
              onClick={handleNextStride}
              className="p-2 rounded-full hover:bg-gray-100"
              disabled={session.strides.indexOf(selectedStride) === session.strides.length - 1}
            >
              <ChevronRight size={24} className={session.strides.indexOf(selectedStride) === session.strides.length - 1 ? "text-gray-300" : "text-gray-600"} />
            </button>
          </div>
        </div>
        
        {/* Metrics and controls */}
        <div className="lg:w-80">
          <div className="bg-white border rounded-lg p-4 mb-4">
            <h3 className="font-medium mb-4 pb-2 border-b">Step Metrics</h3>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-sm text-gray-600">Step Length:</div>
                <div className="text-sm font-medium">{currentStep?.stepLength.toFixed(1)} cm</div>
                
                <div className="text-sm text-gray-600">Duration:</div>
                <div className="text-sm font-medium">{(currentStep?.duration || 0) / 1000} sec</div>
                
                <div className="text-sm text-gray-600">Peak Pressure:</div>
                <div className="text-sm font-medium">{currentStep?.peakPressure.toFixed(1)} N/cm²</div>
                
                <div className="text-sm text-gray-600">Contact Area:</div>
                <div className="text-sm font-medium">{currentStep?.contactArea.toFixed(1)} cm²</div>
                
                <div className="text-sm text-gray-600">Propulsion Force:</div>
                <div className="text-sm font-medium">{currentStep?.propulsionForce.toFixed(1)} N</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-4 pb-2 border-b">Visualization Options</h3>
            
            <div className="space-y-4">
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-2">Pressure Scale:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPressureScale('linear')}
                    className={`px-3 py-1 text-sm rounded ${
                      pressureScale === 'linear' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    Linear
                  </button>
                  <button
                    onClick={() => setPressureScale('logarithmic')}
                    className={`px-3 py-1 text-sm rounded ${
                      pressureScale === 'logarithmic' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    Logarithmic
                  </button>
                </div>
              </div>
              
              <div className="flex flex-col">
                <span className="text-sm text-gray-600 mb-2">Display Mode:</span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setDisplayMode('2d')}
                    className={`px-3 py-1 text-sm rounded ${
                      displayMode === '2d' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    2D View
                  </button>
                  <button
                    onClick={() => setDisplayMode('3d')}
                    className={`px-3 py-1 text-sm rounded ${
                      displayMode === '3d' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300'
                    }`}
                  >
                    3D Effect
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Show Center of Pressure:</span>
                <button 
                  onClick={() => setShowCenterOfPressure(!showCenterOfPressure)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    showCenterOfPressure ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showCenterOfPressure ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-white border rounded-lg p-4 mt-4">
            <h3 className="font-medium mb-2">Legend</h3>
            <div className="flex items-center space-x-2 mt-2">
              <div className="w-full h-6 rounded-md" style={{ 
                background: 'linear-gradient(to right, #c8e6ff, #3b82f6, #ff4444)' 
              }} />
            </div>
            <div className="flex justify-between text-xs text-gray-600 mt-1">
              <span>Low Pressure</span>
              <span>High Pressure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PressureMapping; 